// ---------------------------------------------------------------------------
// Estado de navegação em ÍNDICE ANCORADO (F1 C2).
//
// Alvo e posição da navegação ficam guardados como índice ancorado da vela (0 = vela mais nova na abertura da
// série, cresce para o passado; velas novas ao vivo ganham -1, -2; veja seriesWindow.ts) MAIS uma fração de vela.
// Inteiro + fração separados: a conversão para a "distância do fim" é EXATA (inteiro - inteiro + fração), sem
// arredondamento novo; assim a navegação fica idêntica à de antes, número por número.
//
// Fronteira: o resto do app (cena, fatiador, autoplay, sondas) ainda trabalha em distância do fim. `target` e
// `position` são vistas com `.current` em distância do fim (mesma forma de um useRef<number>): lêem e escrevem o
// estado ancorado convertendo na hora. Zero alocação: getters e setters sobre números.
//
// F2: os consumidores (fatiador, cena, lateral, sondas) não leem mais uma distância global. Cada array de dados
// (série processada, lotes da cena) leva o índice ancorado da SUA vela mais nova (`tagNewestAbs`), e o consumidor
// pede a posição RELATIVA aos seus dados (`relFor` / `positionFor`). Assim dois lotes de idades diferentes (o
// principal e o da parede, adiado) põem a mesma vela no mesmo lugar, chegue vela nova ao vivo ou não.
//
// Etiquetas e chaves usam o índice GLOBAL (= índice ancorado + `base`): o ancorado volta a 0 na troca de série
// (reanchor), o global nunca anda para trás. Assim uma etiqueta ou chave de antes da troca continua valendo depois.
//
// Política de vela nova ao vivo (LIVE_POLICY, uma constante):
//   'acompanhaPresente' (F1): alvo e posição andam junto com o presente; a distância do fim não muda.
//   'mesmasVelas' (F2 T3, em vigor): com a vista no passado, o índice ancorado fica fixo e a distância do fim cresce (a vista
//   não desliza); com a vista no presente (distância < 1: a vela mais nova é a mais nova visível; F3a.1) ela segue o ao vivo.
// ---------------------------------------------------------------------------

export type LivePolicy = 'acompanhaPresente' | 'mesmasVelas';
/**
 * Política de vela nova ao vivo enquanto se vê o passado (F2 T3: 'mesmasVelas', a única mudança visível do F2).
 * Reverter = trocar esta linha para 'acompanhaPresente' (o replay de referência dessa política fica em
 * tests/replay/reference-acompanha.json.gz).
 */
export const LIVE_POLICY = 'mesmasVelas' as LivePolicy;

export interface DistanceRef { current: number; [extra: string]: any }

// Índice ancorado da vela mais nova de cada array de dados (série processada, lote). Sem etiqueta = NaN (o
// consumidor cai na distância do fim de sempre).
const newestTag = new WeakMap<object, number>();
/** Etiqueta um array de dados com o índice GLOBAL da sua vela mais nova (AnchoredNav.newestGlobal). */
export function tagNewestAbs<T extends object>(data: T, newestAbs: number): T { newestTag.set(data, newestAbs); return data; }
// F3b: série de origem de um array de dados (contador de trocas de ativo/timeframe). Dois arrays com o mesmo índice
// global podem ser de séries diferentes (a troca re-ancora: a série nova nasce com o mesmo índice global).
const seriesTag = new WeakMap<object, number>();
/** Etiqueta um array de dados com a série de origem. */
export function tagSeries<T extends object>(data: T, series: number): T { seriesTag.set(data, series); return data; }
/** Série de origem de um array de dados (NaN se não etiquetado). */
export function seriesOf(data: object | null | undefined): number { if (!data) return NaN; const v = seriesTag.get(data); return v === undefined ? NaN : v; }
/** Índice global da vela mais nova de um array de dados (NaN se não etiquetado). */
export function newestAbsOf(data: object | null | undefined): number { if (!data) return NaN; const v = newestTag.get(data); return v === undefined ? NaN : v; }
/** Posição da vista relativa a dados cuja vela mais nova tem índice GLOBAL `dataNewestG` (newestAbsOf; aceita um useRef comum). */
export function positionFor(ref: { current: number } | null | undefined, dataNewestG: number): number {
  if (!ref) return 0;
  const n = (ref as any).nav as AnchoredNav | undefined;
  return n ? n.relFor(dataNewestG) : ref.current;
}
/** O mesmo para o alvo. */
export function targetFor(ref: { current: number } | null | undefined, dataNewestG: number): number {
  if (!ref) return 0;
  const n = (ref as any).nav as AnchoredNav | undefined;
  return n ? n.targetRelFor(dataNewestG) : ref.current;
}
/** Escreve o alvo dado em distância relativa a `dataNewestG` (aceita um useRef comum). */
export function setTargetFor(ref: { current: number } | null | undefined, d: number, dataNewestG: number): void {
  if (!ref) return;
  const n = (ref as any).nav as AnchoredNav | undefined;
  if (n) n.setTargetRel(d, dataNewestG); else ref.current = d;
}
/** Escreve a posição dada em distância relativa a `dataNewestG` (aceita um useRef comum). */
export function setPositionFor(ref: { current: number } | null | undefined, d: number, dataNewestG: number): void {
  if (!ref) return;
  const n = (ref as any).nav as AnchoredNav | undefined;
  if (n) n.setPosRel(d, dataNewestG); else ref.current = d;
}
/**
 * Coordenada de MOVIMENTO da posição (para velocidade): só muda com escrita da navegação, nunca com vela nova ao
 * vivo. Com 'acompanhaPresente' é a própria distância do fim.
 */
export function motionOf(ref: { current: number } | null | undefined): number {
  if (!ref) return 0;
  const n = (ref as any).nav as AnchoredNav | undefined;
  return n ? n.posDist - n.liveDrift : ref.current;
}

/** Vista do ALVO em distância do fim (getter/setter no protótipo: chamada direta, sem alocação). */
class TargetView implements DistanceRef {
  [extra: string]: any;
  constructor(readonly nav: AnchoredNav) {}
  get current(): number { return this.nav.targetDist; }
  set current(d: number) { const n = this.nav, w = Math.floor(d); n.targetAbs = n.newestAbs + w; n.targetFrac = d - w; n.targetDist = d; }
}
/** Vista da POSIÇÃO em distância do fim. */
class PositionView implements DistanceRef {
  [extra: string]: any;
  constructor(readonly nav: AnchoredNav) {}
  get current(): number { return this.nav.posDist; }
  set current(d: number) { const n = this.nav, w = Math.floor(d); n.posAbs = n.newestAbs + w; n.posFrac = d - w; n.posDist = d; }
}

/** Chave do "no presente, seguindo o ao vivo" na política 'mesmasVelas' (não muda quando chega vela nova). */
export const LIVE_KEY = Number.NEGATIVE_INFINITY;
/** Chave inicial do estado React da navegação (no presente) na política em vigor. */
export const INITIAL_HISTORY_KEY = LIVE_POLICY === 'acompanhaPresente' ? 0 : LIVE_KEY;

export class AnchoredNav {
  /** índice ancorado da vela mais nova carregada (0 na abertura; -k depois de k velas novas ao vivo) */
  newestAbs = 0;
  /** alvo: índice ancorado da vela (inteiro) + fração */
  targetAbs = 0; targetFrac = 0;
  /** posição exibida: índice ancorado da vela (inteiro) + fração */
  posAbs = 0; posFrac = 0;
  /**
   * Cópia pronta da distância do fim de alvo e posição (a fronteira lê isto a cada quadro, sem conta nem alocação).
   * É derivada do índice ancorado: (abs - newestAbs) + frac; só muda numa escrita ou quando a vela mais nova muda de
   * índice com a política "fica nas mesmas velas" (F2). Com a política do F1 (acompanha o presente) ela não muda.
   */
  targetDist = 0; posDist = 0;
  /** soma dos deslocamentos das trocas de série: índice global = índice ancorado + base */
  base = 0;
  /** quanto a distância do fim da posição mudou sem movimento (vela nova ao vivo no passado, cargas e despejos) */
  liveDrift = 0;
  /** limite da navegação: índice ancorado mais antigo que a janela visível pode alcançar (null = sem dados) */
  limitAbs: number | null = null;

  /** Vista em distância do fim do ALVO (a forma que a cena e o autoplay usam: targetHistoryOffset.current). */
  readonly target: DistanceRef;
  /** Vista em distância do fim da POSIÇÃO (historyOffsetRef.current). */
  readonly position: DistanceRef;

  /** política de vela nova ao vivo (LIVE_POLICY; os testes podem trocar por instância) */
  policy: LivePolicy = LIVE_POLICY;

  constructor() {
    this.target = new TargetView(this);
    this.position = new PositionView(this);
  }

  /** Índice GLOBAL da vela mais nova (o que vai nas etiquetas dos dados e nas chaves). */
  get newestGlobal(): number { return this.newestAbs + this.base; }

  /** Índice ancorado (com fração) do alvo e da posição. */
  get targetIndex(): number { return this.targetAbs + this.targetFrac; }
  get positionIndex(): number { return this.posAbs + this.posFrac; }

  /**
   * Entraram `k` velas novas ao vivo nos dados processados (a mais nova fica `k` índices mais nova), com a janela de
   * velas contendo o presente.
   * 'acompanhaPresente': alvo e posição andam junto (a distância do fim não muda).
   * 'mesmasVelas': o que está no presente (distância < 1, F3a.1) segue o ao vivo; o resto fica no mesmo índice ancorado e a
   * distância do fim cresce `k`. F3a: a vela mais antiga não sai mais a cada vela nova (a janela despeja por blocos,
   * do lado mais distante da vista), então nada desliza na borda mais antiga e o limite não muda aqui.
   */
  liveAppended(k: number): void {
    if (!(k > 0)) return;
    this.newestAbs -= k;
    if (this.policy === 'acompanhaPresente') { this.targetAbs -= k; this.posAbs -= k; return; }
    // F3a.1 C3: "no presente" = a vela mais nova é a mais nova visível (distância < 1; o fatiador mostra até
    // len - floor(d)). Antes só a distância 0 exata seguia o ao vivo: com o dedo solto a 0,4 vela do fim a vista ficava
    // nas mesmas velas e a vela nova nascia fora da tela (a parede "não recebia" a vela nova no 1m).
    if (this.targetDist < 1) this.targetAbs -= k; else this.targetDist += k;
    if (this.posDist < 1) this.posAbs -= k; else { this.posDist += k; this.liveDrift += k; }
  }

  /**
   * A vela mais nova DOS DADOS passou a ter o índice `newestAbs` por carga ou despejo (bloco mais novo carregado,
   * costura com a cauda ao vivo, despejo das mais novas): alvo e posição ficam nas mesmas velas (índice ancorado), só
   * a distância do fim muda. Não é movimento (a coordenada de movimento não anda).
   */
  dataShifted(newestAbs: number): void {
    if (newestAbs === this.newestAbs) return;
    const p0 = this.posDist;
    this.newestAbs = newestAbs;
    this.targetDist = (this.targetAbs - newestAbs) + this.targetFrac;
    this.posDist = (this.posAbs - newestAbs) + this.posFrac;
    this.liveDrift += this.posDist - p0;
  }

  /**
   * Posição da vista em distância do fim RELATIVA a dados cuja vela mais nova tem índice `dataNewestG`.
   * 'acompanhaPresente' (ou dados sem etiqueta): a distância do fim de sempre, para qualquer dado.
   * 'mesmasVelas': (índice da posição - índice da vela mais nova dos dados) + fração, nunca negativa (dados mais
   * velhos que a posição no presente mostram o próprio presente).
   */
  relFor(dataNewestG: number): number {
    if (this.policy === 'acompanhaPresente' || dataNewestG !== dataNewestG) return this.posDist;
    const d = (this.posAbs + this.base - dataNewestG) + this.posFrac;
    return d > 0 ? d : 0;
  }
  /** O mesmo para o alvo. */
  targetRelFor(dataNewestG: number): number {
    if (this.policy === 'acompanhaPresente' || dataNewestG !== dataNewestG) return this.targetDist;
    const d = (this.targetAbs + this.base - dataNewestG) + this.targetFrac;
    return d > 0 ? d : 0;
  }

  /**
   * Escreve o alvo dado em distância relativa a `dataNewestG` (o arrasto trava contra o total desses dados).
   * 'acompanhaPresente': escrita direta em distância do fim. 'mesmasVelas': guarda o índice ancorado; 0 = presente.
   */
  setTargetRel(d: number, dataNewestG: number): void {
    if (this.policy === 'acompanhaPresente' || dataNewestG !== dataNewestG) { this.target.current = d; return; }
    if (!(d > 0)) { this.targetAbs = this.newestAbs; this.targetFrac = 0; this.targetDist = 0; return; }
    const w = Math.floor(d), abs = dataNewestG - this.base + w, frac = d - w, dist = (abs - this.newestAbs) + frac;
    if (dist > 0) { this.targetAbs = abs; this.targetFrac = frac; this.targetDist = dist; }
    else { this.targetAbs = this.newestAbs; this.targetFrac = 0; this.targetDist = 0; }
  }

  /** O mesmo para a posição. */
  setPosRel(d: number, dataNewestG: number): void {
    if (this.policy === 'acompanhaPresente' || dataNewestG !== dataNewestG) { this.position.current = d; return; }
    if (!(d > 0)) { this.posAbs = this.newestAbs; this.posFrac = 0; this.posDist = 0; return; }
    const w = Math.floor(d), abs = dataNewestG - this.base + w, frac = d - w, dist = (abs - this.newestAbs) + frac;
    if (dist > 0) { this.posAbs = abs; this.posFrac = frac; this.posDist = dist; }
    else { this.posAbs = this.newestAbs; this.posFrac = 0; this.posDist = 0; }
  }

  /**
   * Chave estável de uma posição dada em distância do fim relativa a `dataNewestG` (para estados que precisam
   * sobreviver a vela nova sem render: lote do fatiador, estado React). 'acompanhaPresente': a própria distância;
   * 'mesmasVelas': o índice ancorado. `distOf` faz a volta.
   */
  keyOf(distance: number, dataNewestG: number): number {
    if (this.policy === 'acompanhaPresente') return distance;
    if (distance === 0) return LIVE_KEY;                      // o presente não depende da etiqueta dos dados
    return dataNewestG !== dataNewestG ? distance : dataNewestG + distance;
  }
  distOf(key: number, dataNewestG: number): number {
    if (this.policy === 'acompanhaPresente') return key;
    if (key === LIVE_KEY) return 0;
    if (dataNewestG !== dataNewestG) return key;
    const d = key - dataNewestG;
    return d > 0 ? d : 0;
  }

  /** Série nova (troca de ativo ou timeframe): a vela mais nova volta a ser o índice 0; a distância do fim não muda. */
  reanchor(): void {
    this.base += this.newestAbs;
    this.targetAbs -= this.newestAbs;
    this.posAbs -= this.newestAbs;
    if (this.limitAbs !== null) this.limitAbs -= this.newestAbs;
    this.newestAbs = 0;
  }

  /** Confere a cópia pronta com o índice ancorado (testes): (abs - newestAbs) + frac === distância guardada. */
  consistent(): boolean {
    return (this.targetAbs - this.newestAbs) + this.targetFrac === this.targetDist && (this.posAbs - this.newestAbs) + this.posFrac === this.posDist;
  }

  /** Limite em distância do fim (navTotalPoints - 1000) -> guardado como índice ancorado. */
  setLimitDistance(maxDistance: number | null): void {
    this.limitAbs = maxDistance === null ? null : this.newestAbs + maxDistance;
  }
  /** Limite em distância do fim RELATIVA a dados cuja vela mais nova tem índice global `dataNewestG` (F3a). */
  setLimitFor(maxDistance: number | null, dataNewestG: number): void {
    if (maxDistance === null) { this.limitAbs = null; return; }
    const newest = dataNewestG !== dataNewestG ? this.newestAbs : dataNewestG - this.base;
    this.limitAbs = newest + maxDistance;
  }
  /** Limite em distância do fim (para quem ainda trabalha assim). */
  get limitDistance(): number | null { return this.limitAbs === null ? null : this.limitAbs - this.newestAbs; }
}
